import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CalendarRange, CalendarDays, Copy, Loader2, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Field } from "../common";
import { cn } from "../../lib/utils";
import { addDays, fmtDate, fmtDateLong, parseISODate, JOURS_COURTS } from "../../lib/format";

const WEEKDAY_PRESETS = [
  { label: "Lun – ven", days: [0, 1, 2, 3, 4] },
  { label: "Tous les jours", days: [0, 1, 2, 3, 4, 5, 6] },
];

// Duplique une journée entière (mode="day") ou un seul cours (mode="session")
// vers une date ou une plage de dates, avec aperçu avant validation.
export function DuplicateDialog({ open, onOpenChange, mode = "day", sourceDate, session, nbCours, onDone }) {
  const srcWeekday = sourceDate ? (parseISODate(sourceDate).getDay() + 6) % 7 : 0;
  const [kind, setKind] = useState("date");
  const [date, setDate] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [weekdays, setWeekdays] = useState([0, 1, 2, 3, 4]);
  const [includeEleves, setIncludeEleves] = useState(true);
  const [existing, setExisting] = useState("ajouter");
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !sourceDate) return;
    setKind("date");
    setDate(addDays(sourceDate, 7));
    setStart(addDays(sourceDate, 1));
    setEnd(addDays(sourceDate, 28));
    setWeekdays([srcWeekday]);
    setIncludeEleves(true);
    setExisting("ajouter");
    setPreview(null);
  }, [open, sourceDate, srcWeekday]);

  const payload = useMemo(() => {
    const base = { include_eleves: includeEleves, existing };
    return kind === "date" ? { ...base, date } : { ...base, start, end, weekdays };
  }, [kind, date, start, end, weekdays, includeEleves, existing]);

  const url = mode === "day" ? `/planning/days/${sourceDate}/duplicate` : `/planning/sessions/${session?.id}/duplicate`;

  // Aperçu automatique (sans rien créer) à chaque changement de réglage.
  useEffect(() => {
    if (!open || !sourceDate) return;
    if (kind === "date" ? !date : !(start && end)) { setPreview(null); return; }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post(url, { ...payload, dry_run: true });
        if (!cancelled) { setPreview(data); setPreviewError(""); }
      } catch (err) {
        if (!cancelled) { setPreview(null); setPreviewError(formatApiError(err.response?.data?.detail)); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, url, payload, kind, date, start, end, sourceDate]);

  const toggleDay = (d) => setWeekdays((w) => (w.includes(d) ? w.filter((x) => x !== d) : [...w, d].sort()));

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(url, payload);
      toast.success(`${data.cours_crees} cours créé${data.cours_crees > 1 ? "s" : ""} sur ${data.nb_dates} jour${data.nb_dates > 1 ? "s" : ""}`);
      if (data.cours_ignores) toast.info(`${data.cours_ignores} cours déjà présent${data.cours_ignores > 1 ? "s" : ""} (non recréé${data.cours_ignores > 1 ? "s" : ""})`);
      (data.warnings || []).slice(0, 3).forEach((w) => toast.warning(w, { duration: 8000 }));
      onOpenChange(false);
      onDone && onDone(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const title = mode === "day" ? "Dupliquer la journée" : "Dupliquer le cours";
  const subtitle = mode === "day"
    ? `${nbCours} cours du ${fmtDateLong(sourceDate)}`
    : `${session?.module_nom} · ${session?.heure_debut}–${session?.heure_fin} · ${fmtDateLong(sourceDate)}`;
  const nothing = preview && preview.cours_crees === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="first-letter:uppercase">{subtitle}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2">
          {[
            { id: "date", label: "Une date", icon: CalendarDays },
            { id: "plage", label: "Une plage de dates", icon: CalendarRange },
          ].map((k) => (
            <button key={k.id} type="button" onClick={() => setKind(k.id)} data-testid={`dup-kind-${k.id}`}
              className={cn("flex items-center gap-2 rounded-xl border-2 p-3 text-sm font-medium transition-colors", kind === k.id ? "border-primary bg-primary/5" : "border-border hover:bg-accent/50")}>
              <k.icon size={18} className={kind === k.id ? "text-primary" : "text-muted-foreground"} /> {k.label}
            </button>
          ))}
        </div>

        {kind === "date" ? (
          <Field label="Date cible"><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="dup-date" /></Field>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Du"><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="dup-start" /></Field>
              <Field label="Au"><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="dup-end" /></Field>
            </div>
            <div>
              <p className="text-sm font-medium mb-1.5">Jours concernés</p>
              <div className="flex flex-wrap gap-1.5">
                {JOURS_COURTS.map((j, i) => (
                  <button key={j} type="button" onClick={() => toggleDay(i)} data-testid={`dup-wd-${i}`}
                    className={cn("h-9 w-12 rounded-lg border text-xs font-medium capitalize transition-colors", weekdays.includes(i) ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-accent")}>
                    {j.replace(".", "")}
                  </button>
                ))}
              </div>
              <div className="flex gap-3 mt-2 text-xs">
                <button type="button" className="text-primary hover:underline" onClick={() => setWeekdays([srcWeekday])}>Même jour ({JOURS_COURTS[srcWeekday].replace(".", "")}) chaque semaine</button>
                {WEEKDAY_PRESETS.map((p) => <button key={p.label} type="button" className="text-primary hover:underline" onClick={() => setWeekdays(p.days)}>{p.label}</button>)}
              </div>
            </div>
          </div>
        )}

        <div className="space-y-2 rounded-xl border border-border p-3">
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={includeEleves} onCheckedChange={(v) => setIncludeEleves(!!v)} /> Copier aussi les élèves inscrits</label>
          <div>
            <p className="text-sm mb-1.5">Si des cours existent déjà à ces dates :</p>
            <div className="grid grid-cols-2 gap-2">
              {[["ajouter", "Les garder et ajouter"], ["remplacer", "Les remplacer"]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setExisting(v)} data-testid={`dup-existing-${v}`}
                  className={cn("rounded-lg border px-3 py-2 text-xs font-medium", existing === v ? (v === "remplacer" ? "border-destructive bg-destructive/10 text-destructive" : "border-primary bg-primary/5 text-primary") : "border-border hover:bg-accent")}>
                  {l}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground mt-1.5">Un cours identique (même module, mêmes horaires) n'est jamais créé deux fois le même jour.</p>
          </div>
        </div>

        <div className="rounded-xl bg-muted/50 p-3 text-sm min-h-[64px]" data-testid="dup-preview">
          {loading ? (
            <span className="flex items-center gap-2 text-muted-foreground"><Loader2 size={14} className="animate-spin" /> Calcul de l'aperçu...</span>
          ) : previewError ? (
            <span className="text-destructive">{previewError}</span>
          ) : preview ? (
            <div className="space-y-1.5">
              <p>
                <b>{preview.cours_crees}</b> cours à créer sur <b>{preview.nb_dates}</b> jour{preview.nb_dates > 1 ? "s" : ""}
                {preview.nb_dates > 1 && <> (du {fmtDate(preview.dates[0])} au {fmtDate(preview.dates[preview.dates.length - 1])})</>}
                {preview.cours_ignores > 0 && <span className="text-muted-foreground"> · {preview.cours_ignores} déjà présent{preview.cours_ignores > 1 ? "s" : ""}</span>}
              </p>
              {preview.cours_supprimes > 0 && (
                <p className="text-destructive flex items-center gap-1.5"><AlertTriangle size={14} /> {preview.cours_supprimes} cours existant{preview.cours_supprimes > 1 ? "s" : ""} seront supprimés</p>
              )}
              <div className="flex flex-wrap gap-1 pt-1">
                {preview.par_date.slice(0, 24).map((p) => (
                  <span key={p.date} className={cn("rounded-md border px-1.5 py-0.5 text-[11px] tabular-nums", p.a_creer ? "border-border bg-background" : "border-dashed text-muted-foreground")}>
                    {JOURS_COURTS[(parseISODate(p.date).getDay() + 6) % 7].replace(".", "")} {fmtDate(p.date).slice(0, 5)}
                  </span>
                ))}
                {preview.par_date.length > 24 && <span className="text-[11px] text-muted-foreground">+{preview.par_date.length - 24}</span>}
              </div>
            </div>
          ) : (
            <span className="text-muted-foreground">Choisissez la ou les dates cibles.</span>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={submit} disabled={saving || loading || !preview || nothing} variant={existing === "remplacer" && preview?.cours_supprimes ? "destructive" : "default"} data-testid="dup-submit">
            <Copy size={14} /> {preview ? `Créer ${preview.cours_crees} cours` : "Dupliquer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
